<template>
  <div>
    <v-dialog
      v-model="leaveConfirmDialog"
      width="600"
    >
      <v-card>
        <v-card-title class="primaryGradientBackground whiteText">
          {{ $t('unsavedChanges.title') }}
        </v-card-title>
        <v-card-text class="mt-2">
          {{ $t('unsavedChanges.text') }}
        </v-card-text>
        <v-card-actions>
          <v-btn
            outlined
            color="accent darken-1"
            text
            @click="onStay"
          >
            {{ $t('unsavedChanges.stay') }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            @click="nextRoute()"
          >
            <v-icon left>
              east
            </v-icon>
            {{ $t('unsavedChanges.leave') }}
          </v-btn>
          <v-btn
            color="accent"
            :loading="saving"
            @click="saveAndLeave"
          >
            <v-icon left>
              save
            </v-icon>
            {{ $t('unsavedChanges.saveAndLeave') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <UserLayout
      :article-step="articleStep"
      :seo-score-context="seoScoreContext"
      :disable-save="analyzeExtractionFailed || analyzeExtractionPartial || articleSaving || sharePreview"
    >
      <v-progress-linear
        v-if="loading"
        color="accent"
        indeterminate
      />
      <div v-else>
        <SharePreviewBanner v-if="sharePreview" />
        <v-alert
          v-if="shareError"
          type="error"
          colored-border
          border="left"
          class="mb-6"
        >
          <div class="font-weight-bold mb-1">{{ $t('share.missingTitle') }}</div>
          <div class="body-2 mb-4">{{ $t('share.missingBody') }}</div>
          <v-btn depressed color="primary" class="text-none" @click="goRegister">
            {{ $t('share.register') }}
          </v-btn>
        </v-alert>
        <KeywordForm
          v-else-if="!sharePreview && (!article.keyword || !article.language_code || !article.location_code)"
          @input="onKeywordInput"
        />
        <div v-else-if="!analyzeExtractionFailed && !analyzeExtractionPartial">
          <KeywordSelection
            :article-id="article.id"
            :article-metadata="article"
            :read-only="keywordSelectionReadOnly"
            :ai-prompt-missing="!sharePreview && !!article.selectedKeyword && !article.aiPrompt && !article.noAiPrompt"
            :is-analyze="isArticleAnalyze"
            @input="onKeywordSelected"
            @updateArticleKeywordsForAi="onUpdateArticleKeywordsForAi"
          />
          <ArticleCreation
            v-if="inArticleCreation"
            :preparing-ai="preparingAi"
            :seo-score-context="seoScoreContext"
            :is-analyze="isArticleAnalyze"
            :disable-save="articleSaving"
            @save="onArticleSave"
            @extractionFailed="onExtractionFailed"
            @extractionPartial="onExtractionPartial"
          />
        </div>
        <div
          v-else
          class="d-flex justify-center"
        >
          <v-card
            max-width="800"
            color="warning"
            :class="{'pie-card' : !$vuetify.theme.dark}"
            class="pa-8"
          >
            <v-card-title class="d-flex justify-center white--text pb-8">
              <h1 class="text-center">
                {{
                  analyzeExtractionPartial ? $t('article.analyze.scrapePartial.title') : $t('article.analyze.scrapeFail.title')
                }}
              </h1>
            </v-card-title>
            <v-card-text style="color: #fff">
              {{
                analyzeExtractionPartial ? $t('article.analyze.scrapePartial.description') : $t('article.analyze.scrapeFail.description')
              }}
            </v-card-text>
            <v-card-actions
              class="d-flex flex-column align-center"
              style="row-gap: 12px"
            >
              <div
                v-for="button in analyzeExtractionButtons"
                :key="button.text"
                class="d-flex flex-column align-center"
                style="width: 100%"
              >
                <template v-if="button.show">
                  <v-btn
                    :loading="button.loading"
                    :disabled="button.disabled"
                    block
                    @click="button.click()"
                  >
                    <v-icon>
                      {{ button.icon }}
                    </v-icon>
                    <v-spacer />
                    <span class="pr-6">
                      {{ button.text }}
                    </span>
                    <v-spacer />
                  </v-btn>
                  <small class="whiteText">
                    {{ button.description }}
                  </small>
                </template>
              </div>
            </v-card-actions>
          </v-card>
        </div>
      </div>
    </UserLayout>
  </div>
</template>

<script lang="ts">
    import Vue from 'vue';
    import {Article, ArticleMetadata, ArticleQuestion, KeywordForAi} from "@/model/Article";
    import {Firestore} from "@/plugins/Firestore";
    import KeywordSelection from "@/components/keywords/KeywordSelection.vue";
    import ArticleCreation from "@/components/article/ArticleCreation.vue";
    import UserLayout from "@/layouts/UserLayout.vue";
    import KeywordForm from "@/components/keywords/KeywordForm.vue";
    import SharePreviewBanner from "@/components/article/SharePreviewBanner.vue";
    import {applySharedArticleToStore, fetchSharedArticle, isSharePreviewRoute} from "@/service/articleShare";
    import {NavigationGuardNext, Route} from "vue-router/types/router";
    import {EventBus} from '@/utils/EventBus';
    import {captureException} from "@/plugins/Sentry";
    import {Functions} from "@/plugins/Functions";
    import {Scroll} from "@/config/Behaviour";
    import {rearmGenerationStatusWatch, watchForAi} from "@/service/articleAiContent";
    import {adoptRunInProgress} from "@/service/partsTerminalApply";
    import {raceWithLocalAiTimeout, unlockLocalArticleEditors} from '@/service/aiWaitFallback';
    import {startGenerationRun} from '@/service/generationStart';
    import {seoScoreContext} from "@/model/SeoScore";
    import {getArticleSnapshotHash} from "@/utils/ArticleComputed";
    import {ScrapperResponse} from "@/model/Scrapper";
    import {Toast} from '@/model/Toast';
    import {getBaseUrl, FALLBACK_URL} from '@/utils/ArticleUrl';
    import {articlePositionSources, articleHasWritingContent, healMissingSelectedKeyword} from "@/utils/Article";
    import {focusSeoMetric} from "@/service/seoScoreFocus";
    import {resolveKeywordSaveBranch, shouldPreserveAnalyzeOnKeywordSave, KeywordSaveBranch} from "@/utils/articleWriteCta";
    import {hollowReviewFailure} from "@/service/blockProposals";
    import {commitCoveringArticleLive} from "@/service/confirmGeneratedSave";
    import {isGenerationSaveBlocked, savedRunPastLimit} from "@/service/generationSaveGuard";
    import {dismissSaveHeldToasts, saveHeldState, saveHeldToast} from "@/service/saveHeldToast";
    import {
        clearAnalyzeScrapeFailPending,
        hasAnalyzeScrapeFailPending,
        markAnalyzeScrapeFailPending,
    } from '@/service/analyzeScrapeOutcome';
    import {ArticleCreditUsed, scrapeFailDeleteDescriptionKey} from '@/service/articleCancelRefund';

    type RouteNext = NavigationGuardNext | (() => any);
    type AnalyzeExtractionButton = {
        loading: boolean,
        disabled: boolean,
        text: string,
        description: string,
        show: boolean,
        click: () => void,
        icon: string
    }

    export default Vue.extend({
        name: "ArticleView",
        components: {KeywordForm, UserLayout, ArticleCreation, KeywordSelection, SharePreviewBanner},
        beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext) {
            if (this.sharePreview || to.name === 'SeoScoreDetail') {
                next();
                return;
            }
            this.routeChangeGuard(next);
        },
        beforeRouteUpdate(to: Route, from: Route, next: NavigationGuardNext) {
            this.routeChangeGuard(next);
        },
        data: () => ({
            loading: false,
            preparingAi: false,
            leaveConfirmDialog: false,
            nextRoute: null as null | RouteNext,
            saving: false,
            seoScoreContext: seoScoreContext,
            analyzeExtractionFailed: false,
            analyzeExtractionPartial: false,
            analyzeExtractionButtonsLoading: {
                deleteArticle: false,
                partialContent: false,
                emptyContent: false
            },
            partialContent: null as null | ScrapperResponse,
            shareError: false,
            // What "Delete article" in the scrape dialog returns (#30943).
            scrapeDialogCreditUsed: null as null | ArticleCreditUsed,
            stopScrapeDialogCreditWatch: null as null | (() => void),
        }),
        computed: {
            article: {
                get: function () {
                    return this.$store.getters.article;
                },
                set: function (value: Article) {
                    return this.$store.dispatch('setArticle', value);
                }
            },
            articleId: function (): string {
                return this.$route.params.articleId as string;
            },
            articleStep: function (): number {
                if (!this.article.keyword || !this.article.language_code || !this.article.location_code) {
                    return 1;
                } else if (!this.article.selectedKeyword && !articleHasWritingContent(this.article)) {
                    return 2;
                } else {
                    return 3;
                }
            },
            inArticleCreation: function (): boolean {
                return !!(this.article.selectedKeyword && (this.article.noAiPrompt || this.article.aiPrompt || this.article.metaTitle || articleHasWritingContent(this.article)));
            },
            keywordSelectionReadOnly(): boolean {
                return this.sharePreview || !!this.article.selectedKeyword || articleHasWritingContent(this.article);
            },
            isArticleAnalyze: function (): boolean {
                return this.article.isAnalyze;
            },
            analyzeExtractionButtons: function (): AnalyzeExtractionButton[] {
                return [
                    {
                        loading: this.analyzeExtractionButtonsLoading.partialContent,
                        disabled: this.analyzeExtractionButtonsLoading.deleteArticle || this.analyzeExtractionButtonsLoading.emptyContent,
                        text: this.$t('article.analyze.scrapePartial.openPartial.text') as string,
                        description: this.$t('article.analyze.scrapePartial.openPartial.description') as string,
                        show: this.analyzeExtractionPartial,
                        click: this.openPartialEditors,
                        icon: 'article'
                    },
                    {
                        loading: this.analyzeExtractionButtonsLoading.emptyContent,
                        disabled: this.analyzeExtractionButtonsLoading.deleteArticle || this.analyzeExtractionButtonsLoading.partialContent,
                        text: this.$t('article.analyze.scrapeFail.openEmpty.text') as string,
                        description: this.$t('article.analyze.scrapeFail.openEmpty.description') as string,
                        show: true,
                        click: this.openEmptyEditors,
                        icon: 'edit_document'
                    },
                    {
                        loading: this.analyzeExtractionButtonsLoading.deleteArticle,
                        disabled: this.analyzeExtractionButtonsLoading.partialContent || this.analyzeExtractionButtonsLoading.emptyContent,
                        text: this.$t('article.analyze.scrapeFail.deleteArticle.text') as string,
                        description: this.$t(scrapeFailDeleteDescriptionKey(this.scrapeDialogCreditUsed)) as string,
                        show: true,
                        click: this.deleteArticle,
                        icon: 'delete'
                    },
                ];
            },
            articleSaving: function (): boolean {
                return this.$store.getters.articleSaving;
            },
            sharePreview(): boolean {
                return this.$store.getters.sharePreview || isSharePreviewRoute(this.$route.name);
            },
            generationSettled(): boolean {
                return this.article?.enhanced === 'done' || this.article?.aiGenerateStatus === 'done';
            },
            analyzeExtractionDialogShown(): boolean {
                return this.analyzeExtractionFailed || this.analyzeExtractionPartial;
            },
        },
        watch: {
            analyzeExtractionDialogShown: function (shown: boolean): void {
                this.watchScrapeDialogCredit(shown);
            },
            generationSettled: {
                immediate: true,
                handler: function (done: boolean): void {
                    if (done) {
                        this.dismissSaveHeldToast();
                    }
                },
            },
            '$route.query.seoFocus': function () {
                this.applySeoFocus();
            },
            inArticleCreation: function (visible: boolean) {
                if (visible) {
                    this.applySeoFocus();
                }
            },
            preparingAi: function (busy: boolean) {
                if (!busy) {
                    this.applySeoFocus();
                }
            },
        },
        created: function () {
            if (isSharePreviewRoute(this.$route.name)) {
                this.loadSharedArticle();
            } else if (this.articleId) {
                if (this.$store.getters.authenticated) {
                    this.getArticle();
                } else {
                    this.loading = true;
                    this.$watch('$store.getters.authenticated', (value) => {
                        if (value) {
                            this.getArticle();
                        }
                    });
                }
            } else {
                window.console.error("No article uid provided");
                this.$router.push('/');
            }
            EventBus.$on('onArticleSave', (callback: (() => void), options?: {generationStart?: boolean}) => {
                this.onArticleSave(callback, options);
            });
            EventBus.$on('onArticleLeave', (callback: (() => void)) => {
                this.routeChangeGuard(callback);
            });
        },
        destroyed: function () {
            EventBus.$off('onArticleSave');
            EventBus.$off('onArticleLeave');
            this.watchScrapeDialogCredit(false);
        },
        methods: {
            watchScrapeDialogCredit: function (on: boolean): void {
                if (this.stopScrapeDialogCreditWatch) {
                    this.stopScrapeDialogCreditWatch();
                    this.stopScrapeDialogCreditWatch = null;
                }
                this.scrapeDialogCreditUsed = null;
                if (!on || this.sharePreview || !this.articleId) {
                    return;
                }
                this.stopScrapeDialogCreditWatch = Firestore.createArticleCreditUsedWatcher(this.articleId, (creditUsed) => {
                    this.scrapeDialogCreditUsed = creditUsed;
                });
            },
            loadSharedArticle: function () {
                const token = this.$route.params.token as string;
                this.loading = true;
                this.shareError = false;
                this.article = new Article;
                this.$store.commit('setArticleLastSavedSnapshotHash', 'share-preview');
                fetchSharedArticle(token)
                    .then((article: Article) => {
                        applySharedArticleToStore(article);
                    })
                    .catch(() => {
                        this.shareError = true;
                        this.$store.commit('setSharePreview', {active: true, token});
                    })
                    .finally(() => {
                        this.loading = false;
                        this.applySeoFocus();
                    });
            },
            dismissSaveHeldToast: function (): void {
                const toasts = (this.$store.state.toasts && this.$store.state.toasts.toasts) || [];
                dismissSaveHeldToasts(toasts, (id: number) => {
                    this.$store.dispatch('toasts/remove', id);
                });
            },
            goRegister(): void {
                this.$store.commit('clearSharePreview');
                this.$router.push({name: 'Home'});
            },
            persistHealedKeyword: function (article: Article): void {
                if (this.sharePreview) {
                    return;
                }
                // Keep the editor on the writing step. Do not write Firestore
                // until the user edits or saves.
                healMissingSelectedKeyword(article);
            },
            persistHollowReview: function (article: Article | null): void {
                const failure = hollowReviewFailure(article);
                if (!failure || !article || this.sharePreview) {
                    return;
                }
                article.enhanced = failure.enhanced;
                article.aiGenerateStatus = failure.aiGenerateStatus;
                if (article.id) {
                    void Firestore.updateArticleGenerationStatus(article, failure);
                }
            },
            getArticle: function () {
                if (this.$store.getters.article.id === this.articleId) {
                    this.persistHollowReview(this.article);
                    this.persistHealedKeyword(this.article);
                    this.restoreAnalyzeScrapeFailDialog();
                    this.loading = false;
                    this.applySeoFocus();
                    return;
                }
                this.loading = true;
                this.article = new Article; // clear Store
                this.$store.commit('setArticleLastSavedSnapshotHash', null); // clear Store
                Firestore.getUserArticle(this.articleId)
                    .then((article: Article | null) => {
                        if (article) {
                            const loaded = Article.fromObject(article);
                            this.persistHollowReview(loaded);
                            this.persistHealedKeyword(loaded);
                            const baseline = Article.fromObject(loaded.toObject());
                            const baselineHash = getArticleSnapshotHash(baseline);
                            this.article = loaded;
                            this.restoreAnalyzeScrapeFailDialog();
                            this.$nextTick(() => {
                                baselineHash.then((hash) => {
                                    if (this.article && this.article.id === loaded.id) {
                                        this.$store.commit('setArticleLastSavedSnapshotHash', hash);
                                    }
                                });
                            });
                        } else {
                            window.console.error("Unable to fetch article");
                            this.$router.push('/');
                        }
                    })
                    .catch(error => {
                        this.$store.dispatch('toasts/add', {
                            type: 'error',
                            title: 'article.loadingError.title',
                            content: 'article.loadingError.content',
                        });
                        captureException(error);
                        this.$router.push('/');
                    }).finally(() => {
                        this.loading = false;
                        this.applySeoFocus();
                    });
            },
            /** Resolves true only when the article was written. */
            saveArticle: function (options?: {generationStart?: boolean}): Promise<boolean> {
                if (this.sharePreview) {
                    return Promise.resolve(false);
                }
                EventBus.$emit('debounce-flush'); // flush all debounced emits to get current data
                return new Promise<boolean>((resolve) => {
                    this.$nextTick(() => { // wait for next event cycle for emits to finish
                        this.$store.dispatch('toasts/add', {
                            type: 'info',
                            title: 'article.saving.title',
                            content: 'article.saving.content',
                            dismissible: false
                        } as Toast)
                            .then((toastId) => {
                                let saved = false;
                                commitCoveringArticleLive(this.article, {
                                    markDone: false,
                                    allowWithoutCompletions: true,
                                    generationStart: !!options?.generationStart,
                                })
                                    .then((result) => {
                                        if (result === 'hold') {
                                            if (savedRunPastLimit(this.article)) {
                                                // This tab missed the end of the run: the status
                                                // watch takes the committed article in.
                                                rearmGenerationStatusWatch();
                                            }
                                            this.showSaveHeld(toastId);
                                            return;
                                        }
                                        saved = true;
                                        this.$store.dispatch('setArticleLastSavedSnapshotHash');
                                        this.$store.dispatch('toasts/replace', {
                                            id: toastId,
                                            type: 'success',
                                            title: 'article.saved.title'
                                        } as Toast);
                                    })
                                    .catch(error => {
                                        if (isGenerationSaveBlocked(error)) {
                                            this.showSaveHeld(toastId);
                                            return;
                                        }
                                        this.$store.dispatch('toasts/replace', {
                                            id: toastId,
                                            type: 'error',
                                            title: 'article.saveError.title',
                                            content: 'article.saveError.content',
                                        } as Toast);
                                        captureException(error);
                                    })
                                    .finally(() => resolve(saved));
                            });
                    });
                });
            },
            /** A held save is never silent: the saving toast becomes the held toast. */
            showSaveHeld: function (toastId: number): void {
                this.dismissSaveHeldToast();
                this.$store.dispatch('toasts/replace', {
                    id: toastId,
                    ...saveHeldToast(saveHeldState(this.article)),
                } as Toast);
            },
            saveAndLeave: function () {
                this.saving = true;
                EventBus.$emit('save');
            },
            routeChangeGuard: function (next: RouteNext) {
                if (this.sharePreview || !this.article || !this.article.selectedKeyword || this.analyzeExtractionFailed || this.analyzeExtractionPartial) {
                    next();
                    return;
                }
                // flush all debounced emits to get current data
                EventBus.$emit('debounce-flush');
                // wait for next event cycle for emits to finish
                if (this.inArticleCreation) {
                    this.$nextTick(() => {
                        getArticleSnapshotHash(this.$store.getters.article).then(hash => {
                            if (this.$store.getters.articleLastSavedSnapshotHash !== hash) {
                                this.leaveConfirmDialog = true;
                                this.nextRoute = next;
                            } else {
                                next();
                            }
                        });
                    });
                } else {
                    next();
                }
            },
            onKeywordInput: function (input: ArticleMetadata) {
                this.$set(this.article, 'keyword', input.keyword);
                this.$set(this.article, 'aiPrompt', input.aiPrompt);
                this.$set(this.article, 'noAiPrompt', !input.aiPrompt);
                this.$set(this.article, 'language_code', input.language_code);
                this.$set(this.article, 'location_code', input.location_code);
                this.saveArticle();
            },
            onKeywordSelected: function (
                keyword: string,
                aiPrompt: string,
                questions: ArticleQuestion[],
                keywordsForAi: KeywordForAi[],
                options?: {userConfirmed?: boolean}
            ): Promise<void> {
                const articleId = this.article.id;
                const readSaved = !this.sharePreview && articleId
                    ? () => Firestore.getUserArticle(String(articleId))
                    : undefined;
                return resolveKeywordSaveBranch({
                    inMemoryIsAnalyze: !!this.article.isAnalyze,
                    inMemoryArticle: this.article,
                    readSaved,
                }).catch((): KeywordSaveBranch => {
                    return shouldPreserveAnalyzeOnKeywordSave({
                        inMemoryIsAnalyze: !!this.article.isAnalyze,
                        inMemoryArticle: this.article,
                    }) ? 'analyze' : 'whole';
                }).then((branch) => {
                    return this.commitKeywordSelection(keyword, aiPrompt, questions, keywordsForAi, options, branch);
                });
            },
            commitKeywordSelection: function (
                keyword: string,
                aiPrompt: string,
                questions: ArticleQuestion[],
                keywordsForAi: KeywordForAi[],
                options: {userConfirmed?: boolean} | undefined,
                branch: KeywordSaveBranch
            ): Promise<void> {
                // TODO use proper store commit for all fields
                this.$set(this.article, 'selectedKeyword', keyword);
                this.$set(this.article, 'aiPrompt', aiPrompt);
                this.$set(this.article, 'noAiPrompt', !aiPrompt);
                this.$set(this.article, 'keywordsForAi', keywordsForAi);
                if (branch === 'analyze') {
                    // Keep the persisted analyze flag. Never store the in-memory
                    // default (false) or whole-article mode over a URL-improve doc.
                    this.$set(this.article, 'isAnalyze', true);
                    // null, not undefined: Firestore update throws UndefinedProperties
                    // on an undefined field. parts is the only mode this branch keeps.
                    if (this.article.aiGenerateMode === 'whole'
                        || this.article.aiGenerateMode === null
                        || this.article.aiGenerateMode === undefined) {
                        this.$set(this.article, 'aiGenerateMode', null);
                    }
                } else {
                    this.$set(this.article, 'aiGenerateMode', 'whole');
                    this.$set(this.article, 'polish', 'skipped');
                    this.$set(this.article, 'aiGenerateStatus', 'running');
                }

                // add image to article - if any image is scraped (when analyzing) it gets replaced
                this.$set(this.article, 'images', [{
                    url: (this.article.url !== '' ? getBaseUrl(this.article.url) : FALLBACK_URL) + '/' + keyword.replaceAll(' ', '-').toLowerCase() + '.jpg',
                    alt: keyword
                }]);

                if (branch === 'analyze') {
                    return this.saveArticle().then(() => {
                        if (options && options.userConfirmed) {
                            EventBus.$emit('request-enhance');
                        }
                    });
                }

                for (const [index, question] of questions.entries()) {
                    this.$store.commit('setArticleQuestion', {
                        index: index,
                        questionKey: 'question',
                        value: question.question
                    });
                }
                this.preparingAi = true;
                return this.saveArticle({generationStart: true}).then(() => {
                    if (this.article.id) {
                        const article = this.article;
                        const articleId = this.article.id;
                        // Only a failed start call ends the run failed with Retry. An error
                        // while following a started run must not fail the run the server is
                        // writing: it only unlocks.
                        const generate = startGenerationRun(article, () => Functions.OpenAIArticleGenerate({
                            articleId: articleId
                        }), captureException).then<void[] | 'failed'>((outcome) => {
                            if (outcome === 'failed') {
                                return outcome;
                            }
                            if (outcome === 'following') {
                                // A run started from another tab is in progress: take its clock and follow it.
                                return adoptRunInProgress(article).then(() => watchForAi());
                            }
                            return watchForAi();
                        });
                        raceWithLocalAiTimeout(generate)
                            .then((result) => {
                                if (result === 'timeout') {
                                    unlockLocalArticleEditors();
                                }
                            })
                            .catch((error) => {
                                captureException(error);
                                unlockLocalArticleEditors();
                            })
                            .finally(() => {
                                this.preparingAi = false;
                            });
                        Firestore.updateArticleAIGeneratedAt(this.article);
                    } else {
                        this.preparingAi = false;
                        unlockLocalArticleEditors();
                    }
                    this.$vuetify.goTo('#keywordSelectionContainer', {
                        duration: Scroll.duration,
                        offset: Scroll.offset
                    });
                });
            },
            onArticleSave: function (callback: () => void, options?: {generationStart?: boolean}): Promise<void> {
                return this.saveArticle(options).then((saved) => {
                    if (typeof callback === 'function') {
                        callback();
                    }
                    if (this.saving) {
                        // "Save and leave" leaves only after the article was written.
                        // Otherwise the user stays with the edits and the toast says why.
                        if (saved && this.nextRoute) {
                            this.nextRoute();
                        } else if (!saved) {
                            this.leaveConfirmDialog = false;
                            this.nextRoute = null;
                        }
                        this.saving = false;
                    }
                });
            },
            onStay: function (): void {
                this.leaveConfirmDialog = false;
                this.nextRoute = null;
            },
            restoreAnalyzeScrapeFailDialog: function (): void {
                if (!this.isArticleAnalyze || this.sharePreview || !this.articleId) {
                    return;
                }
                if (!hasAnalyzeScrapeFailPending(this.articleId)) {
                    return;
                }
                this.analyzeExtractionFailed = true;
                this.analyzeExtractionPartial = false;
                this.partialContent = null;
                this.$store.commit('setArticleAnalyzeScrapeLoading', false);
            },
            onExtractionFailed: function () {
                if (this.articleId) {
                    markAnalyzeScrapeFailPending(this.articleId);
                }
                this.$store.commit('setArticleAnalyzeScrapeLoading', false);
                this.analyzeExtractionFailed = true;
            },
            onExtractionPartial: function (response: ScrapperResponse) {
                this.analyzeExtractionPartial = true;
                this.partialContent = response;
            },
            deleteArticle: function () {
                this.analyzeExtractionButtonsLoading.deleteArticle = true;
                Functions.CancelArticle({
                    articleId: this.articleId,
                    force: true
                }).then(() => {
                    clearAnalyzeScrapeFailPending(this.articleId);
                    this.$router.push('/');
                }).catch(error => {
                    this.$store.dispatch("handleApiError", error);
                }).finally(() => {
                    this.analyzeExtractionButtonsLoading.deleteArticle = false;
                });
            },
            openEmptyEditors: function () {
                this.analyzeExtractionButtonsLoading.emptyContent = true;
                this.analyzeExtractionPartial = false;
                this.analyzeExtractionFailed = false;
                this.partialContent = null;
                clearAnalyzeScrapeFailPending(this.articleId);
                this.$store.dispatch('setArticleForcedEmpty')
                    .then(() => {
                        this.$store.commit('setArticleAnalyzeScrapeLoading', false);
                        this.analyzeExtractionButtonsLoading.emptyContent = false;
                    });
            },
            openPartialEditors: function () {
                this.analyzeExtractionButtonsLoading.partialContent = true;
                this.$store.dispatch('setArticleScrapedContent', this.partialContent)
                    .then(() => {
                        Object.values(articlePositionSources).forEach(source => {
                            source.fetch(this.article.id).catch((error) => {
                                window.console.warn('Position fetch failed', error);
                            });
                        });
                        this.$store.commit('setArticleAnalyzeScrapeLoading', false);
                        this.analyzeExtractionButtonsLoading.partialContent = false;
                        this.analyzeExtractionPartial = false;
                    });
            },
            onUpdateArticleKeywordsForAi: function (keywordsForAi: KeywordForAi[]) {
                this.$set(this.article, 'keywordsForAi', keywordsForAi);
            },
            applySeoFocus: function () {
                const focus = this.$route.query.seoFocus as string;
                if (!focus || this.loading || this.preparingAi || !this.inArticleCreation) {
                    return;
                }
                this.$nextTick(() => {
                    setTimeout(() => {
                        focusSeoMetric(focus);
                    }, 400);
                });
            }
        }
    });
</script>

<style lang="sass" scoped>
.whiteText
    color: white !important
</style>
